import { useState } from 'react';
import { CheckCheck } from 'lucide-react';
import { FOCUS_RING } from '../../vessel-changes';
import type { EmployeeNotification } from '../api/employeeNotificationsApi';
import { noticesToShow } from '../model/vesselNotices';
import { VesselNoticeRow } from './VesselNoticeRow';

interface VesselNoticeListProps {
  /** The notices to list (escalations already shown as an alert row left out); null until read. */
  items: EmployeeNotification[] | null;
  /** The server's unread count. */
  unread: number;
  onOpen: (notice: EmployeeNotification) => void;
  onMarkAllRead: () => void;
}

/** The vessel notices under the alerts: unread ones first (collapsed), "Show all", "mark all read". */
export function VesselNoticeList({ items, unread, onOpen, onMarkAllRead }: VesselNoticeListProps) {
  const [showAll, setShowAll] = useState(false);
  const all = items ?? [];
  const shown = noticesToShow(all, showAll);
  const canExpand = all.length > noticesToShow(all, false).length;

  return (
    <section aria-label="Vessel notices">
      <div className="flex items-center gap-2 px-3 pb-1 pt-2">
        <h3 className="text-[11px] font-medium text-slate-500">Vessel notices</h3>
        <span className="ml-auto text-[11px] text-slate-400">{unread ? `${unread} unread` : 'nothing unread'}</span>
        {unread > 0 && (
          <button type="button" title="Mark all vessel notices read" aria-label="Mark all vessel notices read" onClick={onMarkAllRead}
            className={`rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 ${FOCUS_RING}`}>
            <CheckCheck className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <ul className="divide-y divide-slate-50">
        {shown.map((item) => <VesselNoticeRow key={item.id} notice={item} onOpen={onOpen} />)}
      </ul>
      {items && all.length === 0 && <p className="px-3 py-4 text-center text-xs text-slate-500">No vessel notices yet.</p>}
      {!showAll && all.length > 0 && shown.length === 0 && <p className="px-3 py-2 text-xs text-slate-500">Nothing unread.</p>}
      {canExpand && (
        <button type="button" aria-expanded={showAll} onClick={() => setShowAll((value) => !value)}
          className={`w-full px-3 py-1.5 text-left text-[11px] font-medium text-blue-700 hover:bg-slate-50 ${FOCUS_RING}`}>
          {showAll ? 'Show unread only' : 'Show all'}
        </button>
      )}
    </section>
  );
}
